var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { GalaxyApi } from "@/api";
import {} from "@/api/notifications";
import { useResourceWatcher } from "@/composables/resourceWatcher";
import { rethrowSimple } from "@/utils/simple-error";
import { mergeObjectListsById } from "@/utils/utils";
import { useBroadcastsStore } from "./broadcastsStore";
const ACTIVE_POLLING_INTERVAL = 30000; // 30 seconds
const INACTIVE_POLLING_INTERVAL = ACTIVE_POLLING_INTERVAL * 20; // 10 minutes
export const useNotificationsStore = defineStore("notificationsStore", () => {
    const { startWatchingResource: startWatchingNotifications } = useResourceWatcher(getNotificationStatus, {
        shortPollingInterval: ACTIVE_POLLING_INTERVAL,
        longPollingInterval: INACTIVE_POLLING_INTERVAL,
    });
    const broadcastsStore = useBroadcastsStore();
    const totalUnreadCount = ref(0);
    const notifications = ref([]);
    const loadingNotifications = ref(false);
    const lastNotificationUpdate = ref(null);
    const unreadNotifications = computed(() => notifications.value.filter((n) => !n.seen_time));
    function loadNotifications() {
        return __awaiter(this, void 0, void 0, function* () {
            const { data, error } = yield GalaxyApi().GET("/api/notifications");
            if (error) {
                rethrowSimple(error);
            }
            const useNotifications = data; // We are sure this cannot be a broadcast
            notifications.value = mergeObjectListsById(useNotifications, [], "create_time", "desc");
        });
    }
    function getNotificationStatus() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (!lastNotificationUpdate.value) {
                    loadingNotifications.value = true;
                    yield broadcastsStore.loadBroadcasts();
                    yield loadNotifications();
                    updateUnreadCount();
                }
                else {
                    const { data, error } = yield GalaxyApi().GET("/api/notifications/status", {
                        params: {
                            query: {
                                since: lastNotificationUpdate.value.toISOString().replace("Z", ""),
                            },
                        },
                    });
                    if (error) {
                        rethrowSimple(error);
                    }
                    totalUnreadCount.value = data.total_unread_count;
                    notifications.value = mergeObjectListsById(notifications.value, data.notifications, "create_time", "desc");
                    broadcastsStore.updateBroadcasts(data.broadcasts);
                }
                lastNotificationUpdate.value = new Date();
            }
            catch (e) {
                console.error(e);
            }
            finally {
                loadingNotifications.value = false;
            }
        });
    }
    function updateBatchNotification(request) {
        return __awaiter(this, void 0, void 0, function* () {
            const { error } = yield GalaxyApi().PUT("/api/notifications", {
                body: request,
            });
            if (error) {
                rethrowSimple(error);
            }
            if (request.changes.deleted) {
                notifications.value = notifications.value.filter((n) => !request.notification_ids.includes(n.id));
            }
            startWatchingNotifications();
        });
    }
    function updateNotification(notification, changes) {
        return __awaiter(this, void 0, void 0, function* () {
            return updateBatchNotification({ notification_ids: [notification.id], changes });
        });
    }
    function updateUnreadCount() {
        totalUnreadCount.value = notifications.value.filter((n) => !n.seen_time).length;
    }
    return {
        notifications,
        totalUnreadCount,
        unreadNotifications,
        loadingNotifications,
        updateNotification,
        updateBatchNotification,
        startWatchingNotifications,
    };
});
